import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { Project, Section, Subsection, Item, ItemStatus, AISuggestion } from '../models/types';
import { aiService } from '../services/aiService';
import { exportProject } from '../utils/exportUtils';
import { useDebouncedCallback, useDetectSlowRender } from '../hooks/usePerformance';
import { withPerformanceOptimization, memoWithName } from '../utils/withPerformanceOptimization';

// Helper function to determine color based on progress
const getProgressColor = (percentage: number, theme: any) => {
  if (percentage < 25) return theme.palette.error.main;
  if (percentage < 50) return theme.palette.warning.main;
  if (percentage < 75) return theme.palette.info.main;
  return theme.palette.success.main;
};

interface ProjectEditorProps {
  project: Project;
  onSave: (project: Project) => void;
}

// Memoized ItemComponent to prevent unnecessary re-renders
interface ItemProps {
  item: Item;
  section: Section;
  sectionId: string;
  subsection: Subsection;
  subsectionId: string;
  suggestions: AISuggestion[];
  onStatusChange: (sectionId: string, subsectionId: string, itemId: string, status: ItemStatus) => void;
  onDescriptionChange: (sectionId: string, subsectionId: string, itemId: string, description: string) => void;
  onAcceptSuggestion: (suggestion: AISuggestion) => void;
}

const ItemComponent: React.FC<ItemProps> = memo(({
  item,
  section,
  sectionId,
  subsection,
  subsectionId,
  suggestions,
  onStatusChange,
  onDescriptionChange,
  onAcceptSuggestion
}) => {
  const theme = useTheme();
  
  // Find AI suggestions for this item
  const itemSuggestions = suggestions.filter(
    s => s.targetId === item.id && s.targetType === 'ITEM'
  );
  
  // Use debounced callback for description changes to prevent too many updates
  const handleDescriptionChange = useDebouncedCallback(
    (value: string) => {
      onDescriptionChange(sectionId, subsectionId, item.id, value);
    },
    300, // 300ms debounce
    {}, // Default options
    [sectionId, subsectionId, item.id, onDescriptionChange]
  );
  
  const statusColor = () => {
    switch (item.status) {
      case 'COMPLETED': return theme.palette.success.main;
      case 'IN_PROGRESS': return theme.palette.warning.main;
      case 'SKIPPED': return theme.palette.info.main;
      default: return theme.palette.error.main;
    }
  };
  
  return (
    <Card 
      variant="outlined"
      sx={{
        borderColor: item.isRequired ? 'warning.main' : 'divider',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: theme.shadows[2],
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              {item.title}
              {item.isRequired && (
                <Chip 
                  size="small" 
                  label="Required" 
                  color="warning" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter description..."
              variant="outlined"
              defaultValue={item.description || ''}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                },
              }}
            />
          </Box>
          <FormControl sx={{ minWidth: 120, ml: 2 }}>
            <InputLabel id={`status-label-${item.id}`}>Status</InputLabel>
            <Select
              labelId={`status-label-${item.id}`}
              id={`status-${item.id}`}
              value={item.status}
              label="Status"
              onChange={(e) => onStatusChange(
                sectionId,
                subsectionId,
                item.id,
                e.target.value as ItemStatus
              )}
              sx={{
                '& .MuiSelect-select': {
                  color: statusColor(),
                  fontWeight: 500,
                }
              }}
            >
              <MenuItem value="NOT_STARTED">Not Started</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="SKIPPED">Skipped</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {itemSuggestions.length > 0 && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 2, 
              bgcolor: alpha(theme.palette.warning.light, 0.3),
              borderColor: theme.palette.warning.main,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.light, 0.5),
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LightbulbIcon color="warning" sx={{ mr: 1 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  AI Suggestion
                </Typography>
                <Typography variant="body2">
                  {itemSuggestions[0].content}
                </Typography>
              </Box>
              <Tooltip title="Apply Suggestion">
                <IconButton 
                  onClick={() => onAcceptSuggestion(itemSuggestions[0])}
                  color="warning"
                  size="small"
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }
                  }}
                >
                  <AddTaskIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        )}
        
        {item.notes && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Guidance:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.notes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

// Optimize display name for React DevTools
ItemComponent.displayName = 'ItemComponent';

// Memoized SubsectionComponent
interface SubsectionProps {
  subsection: Subsection;
  section: Section;
  sectionId: string;
  suggestions: AISuggestion[];
  onSubsectionExpand: (sectionId: string, subsectionId: string) => void;
  onStatusChange: (sectionId: string, subsectionId: string, itemId: string, status: ItemStatus) => void;
  onDescriptionChange: (sectionId: string, subsectionId: string, itemId: string, description: string) => void;
  onAcceptSuggestion: (suggestion: AISuggestion) => void;
}

const SubsectionComponent = memo(({
  subsection,
  section,
  sectionId,
  suggestions,
  onSubsectionExpand,
  onStatusChange,
  onDescriptionChange,
  onAcceptSuggestion
}: SubsectionProps) => {
  const theme = useTheme();
  const progressColor = getProgressColor(subsection.completionPercentage, theme);
  
  return (
    <Accordion
      key={subsection.id}
      expanded={!!subsection.isExpanded}
      onChange={() => onSubsectionExpand(sectionId, subsection.id)}
      sx={{ 
        mb: 1,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          boxShadow: theme.shadows[1],
        }
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {subsection.name}
        </Typography>
        <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center' }}>
          <Chip 
            label={`${Math.round(subsection.completionPercentage)}%`} 
            size="small"
            sx={{ 
              fontWeight: 'bold',
              backgroundColor: alpha(progressColor, 0.1),
              color: progressColor,
              border: `1px solid ${alpha(progressColor, 0.3)}`,
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          paragraph
          sx={{ mb: 2 }}
        >
          {subsection.description}
        </Typography>
        <Grid container spacing={2}>
          {subsection.items.map(item => (
            <Grid item xs={12} key={item.id}>
              <ItemComponent
                item={item}
                section={section}
                sectionId={sectionId}
                subsection={subsection}
                subsectionId={subsection.id}
                suggestions={suggestions}
                onStatusChange={onStatusChange}
                onDescriptionChange={onDescriptionChange}
                onAcceptSuggestion={onAcceptSuggestion}
              />
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
});

SubsectionComponent.displayName = 'SubsectionComponent';

// Memoized SectionComponent
interface SectionProps {
  section: Section;
  suggestions: AISuggestion[];
  onSectionExpand: (sectionId: string) => void;
  onSubsectionExpand: (sectionId: string, subsectionId: string) => void;
  onStatusChange: (sectionId: string, subsectionId: string, itemId: string, status: ItemStatus) => void;
  onDescriptionChange: (sectionId: string, subsectionId: string, itemId: string, description: string) => void;
  onAcceptSuggestion: (suggestion: AISuggestion) => void;
}

const SectionComponent = memo(({
  section,
  suggestions,
  onSectionExpand,
  onSubsectionExpand,
  onStatusChange,
  onDescriptionChange,
  onAcceptSuggestion
}: SectionProps) => {
  const theme = useTheme();
  const progressColor = getProgressColor(section.completionPercentage, theme);
  
  return (
    <Accordion 
      key={section.id} 
      expanded={!!section.isExpanded}
      onChange={() => onSectionExpand(section.id)}
      sx={{ 
        mb: 2,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          boxShadow: theme.shadows[2],
        }
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {section.name}
        </Typography>
        <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center' }}>
          <Chip 
            label={`${Math.round(section.completionPercentage)}%`} 
            size="small"
            sx={{ 
              fontWeight: 'bold',
              backgroundColor: alpha(progressColor, 0.1),
              color: progressColor,
              border: `1px solid ${alpha(progressColor, 0.3)}`,
              mr: 1
            }}
          />
          {section.isCore && (
            <Chip 
              size="small" 
              label="Core" 
              color="secondary" 
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          paragraph
          sx={{ mb: 3 }}
        >
          {section.description}
        </Typography>
        <Box>
          {section.subsections.map(subsection => (
            <SubsectionComponent
              key={subsection.id}
              subsection={subsection}
              section={section}
              sectionId={section.id}
              suggestions={suggestions}
              onSubsectionExpand={onSubsectionExpand}
              onStatusChange={onStatusChange}
              onDescriptionChange={onDescriptionChange}
              onAcceptSuggestion={onAcceptSuggestion}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

SectionComponent.displayName = 'SectionComponent';

// Main ProjectEditor component
const ProjectEditor: React.FC<ProjectEditorProps> = ({ project, onSave }) => {
  // Use performance monitoring
  useDetectSlowRender('ProjectEditor');
  
  const theme = useTheme();
  // State
  const [currentProject, setCurrentProject] = useState<Project>(project);
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [savePending, setSavePending] = useState(false);
  
  // Initialize when project changes
  useEffect(() => {
    setCurrentProject(project);
    
    // Generate initial suggestions
    const initialSuggestions = aiService.performGapAnalysis(project);
    setSuggestions(initialSuggestions);
  }, [project]);
  
  // Save changes with debouncing to reduce frequent updates
  useEffect(() => {
    if (!savePending) return;
    
    const timeoutId = setTimeout(() => {
      // Recalculate progress
      const updatedProject = aiService.calculateProgress(currentProject);
      onSave(updatedProject);
      setSavePending(false);
    }, 500); // Wait 500ms after last change to save
    
    return () => clearTimeout(timeoutId);
  }, [currentProject, onSave, savePending]);
  
  // Generate content suggestions for empty items
  useEffect(() => {
    const newSuggestions: AISuggestion[] = [];
    
    currentProject.sections.forEach(section => {
      section.subsections.forEach(subsection => {
        subsection.items.forEach(item => {
          // Find if there's already a suggestion for this item
          const hasSuggestion = suggestions.some(
            s => s.targetId === item.id && s.targetType === 'ITEM'
          );
          
          // Generate suggestion for empty items without suggestions
          if (!item.description && !hasSuggestion) {
            const contentSuggestion = aiService.generateInitialContent(item, section, subsection);
            if (contentSuggestion) {
              newSuggestions.push(contentSuggestion);
            }
          }
        });
      });
    });
    
    if (newSuggestions.length > 0) {
      setSuggestions(prev => [...prev, ...newSuggestions]);
    }
  }, [currentProject, suggestions]);
  
  // Memoized handlers to prevent unnecessary re-renders
  const handleSectionExpand = useCallback((sectionId: string) => {
    setCurrentProject(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId
          ? { ...section, isExpanded: !section.isExpanded }
          : section
      ),
    }));
  }, []);
  
  const handleSubsectionExpand = useCallback((sectionId: string, subsectionId: string) => {
    setCurrentProject(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.map(subsection => 
                subsection.id === subsectionId
                  ? { ...subsection, isExpanded: !subsection.isExpanded }
                  : subsection
              ),
            }
          : section
      ),
    }));
  }, []);
  
  const handleItemStatusChange = useCallback((
    sectionId: string,
    subsectionId: string,
    itemId: string,
    newStatus: ItemStatus
  ) => {
    setCurrentProject(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.map(subsection => 
                subsection.id === subsectionId
                  ? {
                      ...subsection,
                      items: subsection.items.map(item => 
                        item.id === itemId
                          ? { 
                              ...item, 
                              status: newStatus,
                              lastModified: new Date(),
                            }
                          : item
                      ),
                    }
                  : subsection
              ),
            }
          : section
      ),
    }));
    setSavePending(true);
  }, []);
  
  const handleItemDescriptionChange = useCallback((
    sectionId: string,
    subsectionId: string,
    itemId: string,
    newDescription: string
  ) => {
    setCurrentProject(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId
          ? {
              ...section,
              subsections: section.subsections.map(subsection => 
                subsection.id === subsectionId
                  ? {
                      ...subsection,
                      items: subsection.items.map(item => 
                        item.id === itemId
                          ? { 
                              ...item, 
                              description: newDescription,
                              lastModified: new Date(),
                            }
                          : item
                      ),
                    }
                  : subsection
              ),
            }
          : section
      ),
    }));
    setSavePending(true);
  }, []);
  
  const handleAcceptSuggestion = useCallback((suggestion: AISuggestion) => {
    // Find the target item
    if (suggestion.targetType === 'ITEM') {
      // Apply the suggestion based on its type
      if (suggestion.suggestionType === 'CONTENT_GENERATION') {
        // Find the item and update its description
        let found = false;
        
        const updatedProject = {
          ...currentProject,
          sections: currentProject.sections.map(section => ({
            ...section,
            subsections: section.subsections.map(subsection => ({
              ...subsection,
              items: subsection.items.map(item => {
                if (item.id === suggestion.targetId) {
                  found = true;
                  return {
                    ...item,
                    description: suggestion.content,
                    lastModified: new Date(),
                  };
                }
                return item;
              }),
            })),
          })),
        };
        
        if (found) {
          setCurrentProject(updatedProject);
          
          // Remove the accepted suggestion
          setSuggestions(prev => 
            prev.filter(s => s.id !== suggestion.id)
          );
          setSavePending(true);
        }
      }
    }
  }, [currentProject]);
  
  return (
    <Box sx={{ p: 3 }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        pb={2}
        sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        }}
      >
        <Typography 
          variant="h5" 
          component="h1"
          sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Project Editor
        </Typography>
        
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<FileDownloadIcon />}
          onClick={() => exportProject(currentProject)}
          sx={{ 
            px: 2.5, 
            py: 1,
            borderRadius: '8px', 
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3,
            }
          }}
        >
          Export Project
        </Button>
      </Box>
      
      {currentProject.sections.map(section => (
        <SectionComponent
          key={section.id}
          section={section}
          suggestions={suggestions}
          onSectionExpand={handleSectionExpand}
          onSubsectionExpand={handleSubsectionExpand}
          onStatusChange={handleItemStatusChange}
          onDescriptionChange={handleItemDescriptionChange}
          onAcceptSuggestion={handleAcceptSuggestion}
        />
      ))}
    </Box>
  );
};

// Apply performance optimizations
export default withPerformanceOptimization(ProjectEditor, 'ProjectEditor');
